import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Layer from '@arcgis/core/layers/Layer';
import { ESkeletonType } from '@norfolk-southern/accessns-components';

import { NSCommons } from '@ruby/configs/common.constants';
import { NSCTMSMapConstants } from '@ruby/configs/ctms-map.constants';
import { NSMapConstants } from '@ruby/configs/map.constants';
import { EMapButtonRef, EMapEnums, EMapSymbol } from '@ruby/shared/enums/map.enums';
import { MapComponent } from '@ruby/shared/map/map.component';
import { IMapConfig } from '@ruby/shared/models/map/map.interface';
import { IGeometry, IShipment, IShipmentAttributes, IShipmentTrace } from '@ruby/shared/models/shipments/shipments.interface';

import esri = __esri;

@Component({
  selector: 'ng-ctms-map',
  templateUrl: './ctms-map.component.html',
  styleUrls: ['./ctms-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CTMSMapComponent implements OnChanges {
  @Input() shipments!: Array<IShipment>;
  @Input() traces?: IShipmentTrace;
  @Input() loading!: boolean;
  @Output() viewRoute = new EventEmitter<IShipmentAttributes>();
  @Output() viewTrainConsist = new EventEmitter<IShipmentAttributes>();
  @Output() viewPermit = new EventEmitter<IShipmentAttributes>();
  @Output() clearRoute = new EventEmitter();
  @ViewChild('mapRef') mapRef!: MapComponent;
  config: IMapConfig = NSCTMSMapConstants.config;
  skeleton: ESkeletonType = ESkeletonType.WIDGET;
  filter: EMapEnums = EMapEnums.ALL;
  mapReady = false;
  loads = 0;
  empties = 0;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mapReady) {
      if (changes.traces?.currentValue) {
        this.setup();
      }
      if (changes.shipments?.currentValue) {
        this.addShipments();
        this.setCounts();
      }
    }
  }

  setup(): void {
    this.mapReady = true;
    if (this.mapRef) {
      this.mapRef.view.graphics.removeAll();
      this.addTracks();
      if (this.traces && this.traces?.checkpoints?.length > 0) {
        const stations: Array<esri.Graphic> = [];
        this.traces.checkpoints.forEach((shipment: IShipment, index: number) => {
          let symbol: EMapSymbol;
          if (index === 0) {
            symbol = EMapSymbol.START;
          } else if (this.traces && index === this.traces.checkpoints.length - 1) {
            symbol = EMapSymbol.END;
          } else {
            symbol = EMapSymbol.CHECKPOINT;
          }
          stations.push(new Graphic({
            symbol: NSMapConstants.getSymbolProperties(symbol),
            attributes: shipment.attributes,
            popupTemplate: NSCTMSMapConstants.getPopupTemplate(symbol),
            geometry: {
              type: 'point', longitude: shipment.geometry.longitude, latitude: shipment.geometry.latitude
            } as IGeometry
          }));
        });
        this.mapRef.view.graphics.addMany(stations);
        if (this.config.disable?.clearRoute) {
          this.config.disable.clearRoute = false;
        }
      }
    }
  }

  /**
   * Clear
   *
   * @summary Remove graphics
   * @param clearAll: boolean
   * @returns void
   */
  clear(clearAll?: boolean): void {
    if (this.config.disable?.clearRoute === false) {
      this.config.disable.clearRoute = true;
    }
    this.mapRef.view.graphics.removeAll();
    this.clearRoute.emit();
    if (clearAll) {
      const featureLayer: FeatureLayer = this.mapRef.map.allLayers.find((lyr: Layer) => lyr.id === 'ctms-feature-layer') as FeatureLayer;
      this.filter = EMapEnums.ALL;
      if (featureLayer) {
        this.mapRef.map.remove(featureLayer);
        featureLayer.destroy();
      }
    }
  }

  /**
   * Set Filter
   *
   * @summary Set Filter
   * @returns void
   */
  setFilter(): void {
    const featureLayer: FeatureLayer = this.mapRef.map.allLayers.find((lyr: Layer) => lyr.id === 'ctms-feature-layer') as FeatureLayer;
    if (this.mapRef && featureLayer) {
      featureLayer.definitionExpression = (this.filter === EMapEnums.ALL) ? '' : `leInd = '${ this.filter }'`;
    }
  }

  private addTracks(): void {
    if (this.traces && this.traces?.tracks.length > 0) {
      const tracks = new Graphic({
        symbol: NSMapConstants.getSymbolProperties(EMapSymbol.TRACK),
        geometry: { type: 'polyline', paths: this.traces.tracks } as esri.GeometryProperties
      });
      this.mapRef.view.graphics.add(tracks);
      this.mapRef.view.goTo(tracks);
    }
  }

  private async addShipments(): Promise<void> {
    if (this.mapRef) {
      const graphics: Array<Graphic> = [];
      this.clear(true);
      this.shipments.forEach((graphic: IShipment) => {
        if (graphic.geometry.latitude && graphic.geometry.longitude) {
          const attributes = {
            ...graphic.attributes,
            eta: (graphic.attributes.eta) ? NSCommons.formatNSDate(graphic.attributes.eta, 'MM/dd/yyyy') : ''
          };
          graphics.push(new Graphic({ ...graphic, attributes }));
        }
      });
      if (graphics.length > 0) {
        const layer = new FeatureLayer(NSCTMSMapConstants.getFeatureLayerProperties(graphics));
        layer.when().then(async (lyr: Layer) => {
          if (!this.traces) {
            this.mapRef.view.goTo(lyr.fullExtent);
          }
        });
        this.registerEvents();
        this.mapRef.map.add(layer);
      }
    }
  }

  private registerEvents(): void {
    this.mapRef.view.popup.on('trigger-action', (event: esri.PopupTriggerActionEvent) => {
      const id = event.action.id as EMapButtonRef;
      const selected = this.mapRef.view.popup.selectedFeature.attributes as IShipmentAttributes;
      if (id === EMapButtonRef.PERMIT || id === EMapButtonRef.ROUTE || id === EMapButtonRef.TRAIN_CONSIST) {
        this.mapRef.view.popup.close();
        switch (id) {
          case EMapButtonRef.PERMIT:
            this.viewPermit.emit(selected);
            break;
          case EMapButtonRef.ROUTE:
            this.viewRoute.emit(selected);
            break;
          case EMapButtonRef.TRAIN_CONSIST:
            this.viewTrainConsist.emit(selected);
            break;
        }
      }
    });
  }

  private setCounts(): void {
    this.loads = 0;
    this.empties = 0;
    this.shipments?.forEach((shipment: IShipment) =>
      (shipment.attributes.leInd === EMapEnums.LOAD) ? this.loads++ : this.empties++);
  }
}
